import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  Menu,
  MenuItem,
  Grow,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import { StyledContainer } from '../../dynamicform/styled.js';
import AlertDialog from '../shared/AlertDialog.jsx';
import FaqModal from './FaqModal.jsx';
import { validateAuth, getApiBaseUrl } from '../../util/util.js';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { userTypeSelector } from '../../redux/selectors.js';
import AddBoxIcon from '@mui/icons-material/AddBox';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FAQ_Categories } from '../../util/constants';
import { notificationSuccess, notificationError } from '../../redux/actions/app.js';
import { renderWithLineBreaks } from '../../util/util.js';

export default function FaqTab({ page }) {
  const [faqItems, setFaqItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('General');
  const [hoveredQuestion, setHoveredQuestion] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const currentUserType = useSelector(userTypeSelector);
  const theme = useTheme();
  const dispatch = useDispatch();

  const filteredHelpItems = faqItems.filter((item) => item.category === selectedCategory);

  const handleModalOpen = (modalType) => {
    if (modalType === 'edit') {
      setIsFaqModalOpen(true);
    } else if (modalType === 'delete') {
      setIsAlertModalOpen(true);
    }
    closeMenu();
  };

  const handleModalClose = async (modalType) => {
    if (modalType === 'edit') {
      setIsFaqModalOpen(false);
    } else if (modalType === 'delete') {
      setIsAlertModalOpen(false);
    }

    await getFaqItems();
    setSelectedFaq(null);
  };

  const openMenu = (event, faqItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedFaq(faqItem);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setHoveredQuestion(null);
  };

  const getFaqItems = async () => {
    return validateAuth(async (session) => {
      try {
        const response = await axios.get(`${getApiBaseUrl(currentUserType.type)}/help/faq`, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15000,
        });

        setFaqItems(response?.data?.data || []);
      } catch (error) {
        setFaqItems([]);
      }
    });
  };

  const deleteFaqItems = async (faqItem) => {
    if (currentUserType.type !== 'internal') return;
    return validateAuth(async (session) => {
      try {
        await axios.delete(`${process.env.REACT_APP_AWS_API_URL}/help/faq/${faqItem.id}`, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15000,
        });
        dispatch(notificationSuccess('FAQ item deleted successfully'));
      } catch (error) {
        dispatch(notificationError('Error deleting FAQ item'));
      }
    });
  };

  useEffect(() => {
    getFaqItems();
  }, []);

  return (
    <>
      <Box
        sx={{
          px: theme.spacing(3),
          pb: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <ToggleButtonGroup
          exclusive
          value={selectedCategory}
          onChange={(_, value) => value && setSelectedCategory(value)}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            '& .MuiToggleButton-root': {
              border: 0,
              px: 2,
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          {FAQ_Categories.map((category) => (
            <ToggleButton key={category} value={category}>
              {category}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {currentUserType?.type === 'internal' && page === 'settings' && (
          <Button
            variant="contained"
            disableElevation
            onClick={() => handleModalOpen('edit')}
            startIcon={<AddBoxIcon />}
          >
            Add FAQ Items
          </Button>
        )}
      </Box>
      <Grid
        container
        spacing={5}
        sx={{ paddingLeft: theme.spacing(3), paddingRight: theme.spacing(3) }}
        overflow="scroll"
      >
        {filteredHelpItems.length > 0 ? (
          filteredHelpItems.map((faqItem) => (
            <Grid size={{ xs: 12, md: 4 }} key={faqItem.id}>
              <StyledContainer
                style={{
                  padding: theme.spacing(3),
                  display: 'flex',
                  alignItems: 'flex-start',
                  position: 'relative',
                }}
                onMouseEnter={() => setHoveredQuestion(faqItem.question)}
                onMouseLeave={() => setHoveredQuestion(null)}
              >
                {currentUserType?.type === 'internal' && page === 'settings' && (
                  <Grow
                    in={hoveredQuestion === faqItem.question}
                    sx={{ position: 'absolute', bottom: 8, right: 8 }}
                  >
                    <Tooltip arrow title="Options">
                      <IconButton size="small" onClick={(e) => openMenu(e, faqItem)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grow>
                )}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.35,
                      mb: 1,
                      color: 'text.primary',
                    }}
                  >
                    {faqItem.question}
                  </Typography>
                  <Typography variant="body2">{renderWithLineBreaks(faqItem.answer)}</Typography>
                </Box>
              </StyledContainer>
            </Grid>
          ))
        ) : (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              FAQ not available.
            </Typography>
          </Grid>
        )}
      </Grid>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            handleModalOpen('edit');
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleModalOpen('delete');
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <AlertDialog
        open={isAlertModalOpen}
        onClose={() => handleModalClose('delete')}
        title="Delete this FAQ?"
        cancelLabel="Cancel"
        submitLabel="Delete"
        onCancel={() => handleModalClose('delete')}
        onSubmit={async () => {
          await deleteFaqItems(selectedFaq);
          handleModalClose('delete');
        }}
      >
        {selectedFaq
          ? `Are you sure you want to delete "${selectedFaq.question}"?`
          : 'Are you sure you want to delete this FAQ item?'}
      </AlertDialog>
      <FaqModal
        open={isFaqModalOpen}
        onClose={() => handleModalClose('edit')}
        selectedFaq={selectedFaq}
      />
    </>
  );
}
